import React, { useState } from 'react';
import { Mail, CheckCircle, Copy } from 'lucide-react';
import { cleanExclusionItems } from '../../lib/exclusions';

// Email View Component - Combines Terms and Exclusions for easy copy/paste to email
const EmailView = ({ termsData, exclusionsData, projectName, isNewClient }) => {
    const [copied, setCopied] = useState(false);
    
    // Generate email-formatted content
    const generateEmailContent = () => {
        let content = [];
        
        // Standard intro paragraph
        content.push('Thank you for partnering with ABS. We look forward to a successful project together.');
        content.push('');
        content.push('We have completed our review of the contract documents and identified several items requiring clarification prior to execution. To streamline review, our comments are organized by topic below.');
        content.push('');
        content.push('');
        
        // Project header (no bullet on main section)
        if (projectName) {
            content.push(`𝗣𝗥𝗢𝗝𝗘𝗖𝗧: ${projectName}`);
            content.push('');
        }
        
        // TERMS SECTION (no bullet on main section)
        if (termsData && termsData.length > 0 || isNewClient) {
            content.push('𝗧𝗘𝗥𝗠𝗦');
            content.push('');
            content.push('The following terms reflect clarifications included in our proposal. We request confirmation prior to execution.');
            content.push('');
            
            // Regular terms items - shortened format
            if (termsData && termsData.length > 0) {
                termsData.forEach((item) => {
                    // Skip Post-Award Deliverables from email
                    if (item.title?.toLowerCase().includes('post-award') || 
                        item.title?.toLowerCase().includes('post award')) {
                        return;
                    }
                    
                    // Title with contract section inline
                    const sectionRef = item.contract_section || item.clause_reference || '';
                    const titleLine = sectionRef ? `${item.title}: ${sectionRef}` : item.title;
                    content.push(titleLine);
                    
                    // Response (the main statement/request).
                    // Prefer the shortened email version when the rule provides one.
                    const responseText = item.email_short || item.clarification || item.proposal_text || '';
                    if (responseText) {
                        content.push(`• Response: ${responseText}`);
                    }
                    
                    // Comment - skip for stored material, prime contract/agreement, material escalation
                    const skipComment = item.title?.toLowerCase().includes('offsite storage') ||
                                       item.title?.toLowerCase().includes('stored material') ||
                                       item.title?.toLowerCase().includes('prime') ||
                                       item.title?.toLowerCase().includes('material escalation');
                    
                    const commentText = item.comment || item.reason;
                    if (commentText && !skipComment) {
                        content.push(`• Comment: ${commentText}`);
                    }
                    content.push('');
                });
            }
            
            // New Client Retainer at bottom of TERMS (if checkbox is checked)
            if (isNewClient) {
                content.push('New Client Retainer');
                content.push('• Response: A new client retainer applies and will be included in the Schedule of Values.');
                content.push('');
            }
        }
        
        // EXCLUSIONS SECTION (no bullet on main section, always at the end)
        if (exclusionsData) {
            // Per user direction: do NOT consolidate duplicated exclusions.
            // Render each scope's exclusions verbatim from the proposal so nothing
            // is moved or merged. The reconciler on the backend handles scope
            // attribution; the email just renders what's there.
            // Bare "EXCLUDES:" style header labels are filtered out.
            const generalExclusions = cleanExclusionItems(exclusionsData.general_exclusions);
            const scopeExclusions = (exclusionsData.exclusions_by_scope || [])
                .map(g => ({
                    ...g,
                    exclusions: cleanExclusionItems(g.exclusions),
                }))
                .filter(g => g.exclusions.length > 0);

            const totalExclusions = generalExclusions.length + 
                scopeExclusions.reduce((sum, g) => sum + (g.exclusions?.length || 0), 0);
            
            if (totalExclusions > 0) {
                content.push('𝗣𝗥𝗢𝗣𝗢𝗦𝗔𝗟 𝗘𝗫𝗖𝗟𝗨𝗦𝗜𝗢𝗡𝗦');
                content.push('');
                content.push('The following exclusions were included in our original proposal and may not be fully reflected in the contract documents. We request confirmation that these items are incorporated prior to execution.');
                content.push('');
                
                // General exclusions first
                if (generalExclusions.length > 0) {
                    content.push('General Exclusions');
                    generalExclusions.forEach((exc) => {
                        content.push(`• ${exc}`);
                    });
                    content.push('');
                }
                
                // Scope-specific exclusions
                scopeExclusions.forEach((scopeGroup) => {
                    if (scopeGroup.exclusions && scopeGroup.exclusions.length > 0) {
                        content.push(scopeGroup.scope_name);
                        scopeGroup.exclusions.forEach((exc) => {
                            content.push(`• ${exc}`);
                        });
                        content.push('');
                    }
                });
                
                // Confirmation request at end of exclusions
                content.push('Please reply confirming you acknowledge and agree to the exclusions outlined.');
                content.push('');
            }
        }
        
        // No data message
        if ((!termsData || termsData.length === 0) && !exclusionsData) {
            content.push('No Terms or Exclusions data available.');
            content.push('Please run a Contract Review and/or upload a Proposal first.');
        }
        
        return content.join('\n');
    };
    
    const emailContent = generateEmailContent();
    
    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(emailContent);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = emailContent;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };
    
    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Mail size={20} />
                        Email Export
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Copy Terms and Exclusions to paste into your email
                    </p>
                </div>
                <button
                    onClick={copyToClipboard}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                        copied 
                            ? 'bg-green-600 text-white' 
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                    {copied ? (
                        <>
                            <CheckCircle size={16} />
                            Copied!
                        </>
                    ) : (
                        <>
                            <Copy size={16} />
                            Copy to Clipboard
                        </>
                    )}
                </button>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-1">
                <div className="bg-white rounded border border-gray-100 p-6 overflow-auto max-h-[calc(100vh-280px)]">
                    <pre 
                        className="text-[#1a1a1a] dark:text-[#e2e8f2]"
                        style={{ 
                            fontFamily: 'Aptos, Calibri, Arial, sans-serif',
                            fontSize: '11pt',
                            lineHeight: '1.5',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            margin: 0
                        }}
                    >
                        {emailContent}
                    </pre>
                </div>
            </div>
            
            <div className="text-xs text-gray-400 text-center">
                Font: Aptos, 11pt • Headers formatted as bullets • Ready for email paste
            </div>
        </div>
    );
};

export default EmailView;
